import { Component, computed, signal, Signal } from '@angular/core';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { MultiSelectModule } from 'primeng/multiselect';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';

import { SMSCertifiedNode } from '@modules/campaigns/Domain/Nodes/SMSCertifiedNode';
import { SMSCertifiedConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';
import { InspectorBase } from '../inspector-base';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';

@Component({
  selector: 'app-sms-certified-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    LucideIconsModule,
    InputNumberModule,
    InputSwitchModule,
    MessagesModule,
    MultiSelectModule,
    CalendarModule,
    RadioButtonModule,
    DatePipe,
  ],
  templateUrl: './sms-certified-inspector.component.html',
  styleUrls: ['./sms-certified-inspector.component.scss'],
})
export class SmsCertifiedInspectorComponent extends InspectorBase<SMSCertifiedNode> {
  today = new Date();
  expirationDays = signal<number | null>(180);
  expirationAt = signal<Date | null>(null);
  expirationMode = signal<'days' | 'date'>('days');
  sendToAllContacts = signal<boolean>(false);
  templates = signal<CopyModel[]>([]);

  get expirationModeOptions(): { label: string; value: 'days' | 'date'; icon: string }[] {
    return [
      {
        label: this.translate.instant(`${this.i18nPrefix}.CONFIG.EXPIRATION_MODE_DAYS`),
        value: 'days' as const,
        icon: 'pi pi-clock',
      },
      {
        label: this.translate.instant(`${this.i18nPrefix}.CONFIG.EXPIRATION_MODE_DATE`),
        value: 'date' as const,
        icon: 'pi pi-calendar',
      },
    ];
  }

  readonly selectedTemplates = computed(() => {
    const ids = this.copyIds;
    return this.templates().filter((t) => ids.includes(t.id));
  });

  readonly selectedTemplate = computed(() => this.selectedTemplates()[0] ?? null);
  readonly selectedTemplateName = computed(() => this.selectedTemplate()?.name ?? '');

  readonly editTemplateUrl = computed(() => {
    const ids = this.copyIds;
    if (!ids.length) return null;
    const tenantId = this.route.snapshot.queryParams['tenant'];
    return `/copy/edit/${ids[0]}?tenant=${tenantId}`;
  });

  readonly previewMessage = computed(() => this.extractPreviewMessage(this.selectedTemplate()));

  protected get channelType() {
    return 'certified_sms';
  }
  protected get i18nPrefix() {
    return 'DATA.MODULES.INSPECTORS.SMS_CERTIFIED';
  }

  readonly selectedTemplateIds = signal<string[]>([]);

  get copyIds(): string[] {
    return this.selectedTemplateIds();
  }

  protected setCopyIds(ids: string[]) {
    this.selectedTemplateIds.set(ids);
  }

  override get nodeValue(): SMSCertifiedNode | null {
    const n = this.node();
    return n?.type === NodeType.SendCertifiedSMS ? (n as SMSCertifiedNode) : null;
  }

  protected buildConfig(node: SMSCertifiedNode, copyIds: string[]): SMSCertifiedConfig {
    return {
      ...(node.config as SMSCertifiedConfig),
      copyIds,
      certified: true,
    } as SMSCertifiedConfig;
  }

  protected extractPreviewMessage(template: CopyModel | null): string {
    if (!template) return '';
    const block = template.blocks?.find(
      (b) =>
        b.type === 'certifiedSmsText' ||
        b.alias === 'certifiedSmsText' ||
        b.type === 'smsText' ||
        b.alias === 'smsText' ||
        b.type === 'text',
    );
    return block?.translations?.[0]?.text ?? '';
  }

  onTemplatesChange(ids: string[]) {
    this.selectedTemplateIds.set(ids);
    this.applyChanges();
  }

  override getTabs() {
    return [
      {
        label: this.translate.instant(`${this.i18nPrefix}.TABS.PREVIEW`),
        value: 'preview' as const,
      },
      { label: this.translate.instant(`${this.i18nPrefix}.TABS.CONFIG`), value: 'config' as const },
    ];
  }

  loadTemplates() {
    this.copiesService.searchCopies().subscribe((copies: CopyModel[]) => {
      this.templates.set(copies.filter((c) => c.type === this.channelType));
    });
  }

  override setNode(nodeSignal: Signal<NodeClass | null>) {
    this.node.set(nodeSignal());
    this.refreshFromConfig();
    this.loadTemplates();
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const currentNodes = this.workflow.nodes();
    const currentNode = this.node();
    const nodeValue = currentNodes.find((n) => n.id === currentNode?.id) as SMSCertifiedNode;

    if (!nodeValue) {
      console.warn('Cannot apply changes: nodeValue is null');
      return;
    }

    const expAt = this.expirationMode() === 'date' ? this.expirationAt() : null;
    const expDays = expAt ? 0 : this.expirationDays() ?? 0;

    const baseConfig = (nodeValue.config as SMSCertifiedConfig) ?? {};

    const updatedConfig: SMSCertifiedConfig = {
      ...baseConfig,
      ...this.buildConfig(nodeValue, this.copyIds),
      expirationDays: expDays,
      expirationAt: expAt,
      sendToAllContacts: this.sendToAllContacts(),
      copyIds: this.selectedTemplateIds(),
    } as SMSCertifiedConfig;

    const updated = nodeValue.cloneWithConfig(updatedConfig);
    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
      this.node.set(updated);
    }
  }

  override refreshFromConfig() {
    const nodeValue = this.nodeValue;
    if (!nodeValue) return;
    const cfg = (nodeValue.config ?? {}) as SMSCertifiedConfig;

    this.selectedTemplateIds.set(cfg.copyIds ?? []);
    this.expirationDays.set((cfg as any).expirationDays ?? 180);
    this.expirationAt.set((cfg as any).expirationAt ? new Date((cfg as any).expirationAt) : null);
    this.expirationMode.set((cfg as any).expirationAt ? 'date' : 'days');
    this.sendToAllContacts.set((cfg as any).sendToAllContacts ?? false);
    this.errors.set([]);
  }

  onExpirationModeChange(mode: 'days' | 'date') {
    this.expirationMode.set(mode);
    this.expirationDays.set(180);
    this.scheduleApplyChanges();
  }

  onExpirationDaysChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 0;
    value = Math.max(0, Math.min(365, value));
    this.expirationDays.set(value);
    this.scheduleApplyChanges();
  }

  onExpirationAtChange(value: Date | null) {
    this.expirationAt.set(value);
    this.scheduleApplyChanges();
  }

  onSendToAllContactsChange(value: boolean) {
    this.sendToAllContacts.set(!!value);
    this.scheduleApplyChanges();
  }

  constructor(
    protected readonly workflow: WorkflowStateService,
    protected readonly translate: TranslateService,
    protected readonly copiesService: CopiesService,
    protected readonly route: ActivatedRoute,
  ) {
    super();
  }
}
